import React from "react"

const IMAGE_TREATMENT = {
  "CROP": "CROP",
  "RESIZE": "RESIZE",
  "NONE": "NONE"
}

function StoryblokImageBasic({ image, width, height, sizes }) {
  if(!image) {
    return (<span>INVALID STORYBLOK IMAGE</span>)
  }

  if(sizes) {
    return buildSrcSet(sizes, image, width, height, image ? image.alt : "")
  }
  
  const data = GetImageUrlAndTagSize(image, width, height);
  return (
    <picture>
      <img src={data.url} alt={image ? image.alt : null} width={data.width || null} height={data.height || null} loading="lazy" />
    </picture>
  )
}

/// This function can be used if you want all the url modifications but not a component
function GetImageUrlAndTagSize(image, width, height) {
  const dimensions = DetermineNewDimensions(image, width, height);
  const newUrl = AddStoryblokImageServiceWebpSupport(image.filename);
  let adjustments = "";
  if(dimensions.type === IMAGE_TREATMENT.CROP) {
    adjustments = `${dimensions.left}x${dimensions.top}:${dimensions.right}x${dimensions.bottom}/`
  } else if(dimensions.type === IMAGE_TREATMENT.RESIZE) {
    adjustments = `${dimensions.url_width}x${dimensions.url_height}`
  }
  return {
    url: newUrl+adjustments,
    filename: image.filename,
    adjustments,
    width: dimensions.width,
    height: dimensions.height
  }
}
// If a width and a height are provided this will prefer to crop the image
// If only one value is provided this will prefer to resize
function DetermineNewDimensions(image, preferredWidth, preferredHeight) {
  if(!image) { console.warn("Warning: You are asking for image dimension without passing an image."); return {} }

  let imageProperties = getImagePropertiesFromURL(image.filename);
  let newWidth = preferredWidth,
      newHeight = preferredHeight;
  if(!preferredWidth && !preferredHeight) {
    if(imageProperties) {
      return {
        type: IMAGE_TREATMENT.NONE,
        width: imageProperties.metadata.dimensions.width,
        height: imageProperties.metadata.dimensions.height
      }
    }
    return {
      type: IMAGE_TREATMENT.NONE
    }
  }
  // Don't request images larger than their original size!
  if(preferredWidth) {
    newWidth = Math.min(preferredWidth, imageProperties.metadata.dimensions.width);
  }
  if(preferredHeight) {
    newHeight = Math.min(preferredHeight, imageProperties.metadata.dimensions.height);
  }
  if(preferredWidth && preferredHeight) {
    // LETS CROP
    let focusX = 0,
        focusY = 0;
    if(image.focus) {
      // User selected focal point
      const focusPoint = image.focus.split(":")[0].split("x")
      focusX = Number(focusPoint[0]);
      focusY = Number(focusPoint[1]);
    } else {
      // Center focal point
      focusX = Math.floor(preferredWidth/2);
      focusY = Math.floor(preferredHeight/2);
    }
    // Calculate edges
    let left  = Math.ceil(focusX - (newWidth / 2)),
        right = Math.floor(focusX + (newWidth / 2)),
        top   = Math.ceil(focusY - (newHeight / 2)),
        bot   = Math.floor(focusY + (newHeight / 2));
    // Check if outside of image size
    if(left < 0) {
      // Slide Right
      let horizontalAdjustment = Math.abs(left);
      left = left + horizontalAdjustment;
      right = right + horizontalAdjustment;
    }
    if(right > newWidth) {
      // Slide left
      let horizontalAdjustment = newWidth - right;
      left = left - horizontalAdjustment;
      right = right - horizontalAdjustment;
    }
    if(top < 0) {
      // Slide Down
      let verticalAdjustment = Math.abs(top);
      top = top + verticalAdjustment;
      bot = bot + verticalAdjustment;
    }
    if(bot > newHeight) {
      // Slide Up
      let verticalAdjustment = newHeight - bot
      top = top - verticalAdjustment;
      bot = bot - verticalAdjustment;
    }
    if (left < 0 || top < 0 || right > newWidth || bot > newHeight) {
      console.warn('Image crop does not fit within the provided image dimensions')
      // Returns a resize request instead of a crop
      return DimensionsToResizeObject(imageProperties, newWidth, newHeight);
    }
    return {
      type: IMAGE_TREATMENT.CROP,
      width: (right - left),
      height: (bot - top),
      left: left,
      right: right,
      top: top,
      bottom: bot
    }
  } else {
    // LETS RESIZE
    return DimensionsToResizeObject(imageProperties, newWidth, newHeight)
  }
}
function DimensionsToResizeObject(imageProperties, newWidth, newHeight) {
  if(newHeight) {
    return {
      type: IMAGE_TREATMENT.RESIZE,
      height: newHeight,
      width: Math.floor(newHeight * imageProperties.metadata.dimensions.aspectRatio),
      url_width: 0,
      url_height: newHeight
    }
  } else {
    return {
      type: IMAGE_TREATMENT.RESIZE,
      height: Math.floor(newWidth / imageProperties.metadata.dimensions.aspectRatio),
      width: newWidth,
      url_width: newWidth,
      url_height: 0
    }
  }
}




function buildSrcSet(sizes, image, width, height, alt) {
  let url = AddStoryblokImageServiceWebpSupport(image.filename)
  let props = getImagePropertiesFromURL(url);

  // Get width and height from props above or default to the dimensions from the image url
  let defaultWidth = width ? width : props? props.metadata.dimensions.width : "100%";
  let defaultHeight = height ? height : props ? props.metadata.dimensions.height : "auto";


  return BuildPictureSourceWithSizes(sizes, image, url, defaultWidth, defaultHeight, alt)
}

function BuildPictureSourceWithSizes(sizes, image, url, defaultWidth, defaultHeight, alt) {
  return (
    <picture>
      {Object.keys(sizes).map((breakpoint) => {
        var width = sizes[breakpoint].split("x")[0];
        var height = sizes[breakpoint].split("x")[1];
        var cropFilter = `${width}x${height}`;
        if(image.focus) {
          cropFilter = calcCrop(width, height, image.focus.split(":")[0], defaultWidth, defaultHeight);
        }
        
        if(breakpoint.includes("-")){
          var minMax = breakpoint.split("-");
          var min = minMax[0];
          var max = minMax[1];
          if (min && max) {
            return (
              <source
                media={`(min-width: ${min}px) and (max-width:${max}px)`}
                srcSet={`${image.filename}/m/${cropFilter}/`}
                key={breakpoint + ":" + sizes[breakpoint]}
              />
            );
          }
          if (min && !max) {
            return (
              <source
                media={`(min-width: ${min}px)`}
                srcSet={AddWidthHeightResizeToUrl(image.filename,width,height)}
                key={breakpoint + ":" + sizes[breakpoint]}
              />
            );
          }
          if (!min && max) {
            return (
              <source
                media={`(max-width:${max}px)`}
                srcSet={AddWidthHeightResizeToUrl(image.filename,width,height)}
                key={breakpoint + ":" + sizes[breakpoint]}
              />
            );
          }
        }
        return null
      })}
      <img src={url} alt={alt || null} width={defaultWidth || null} height={defaultHeight || null} loading="lazy" />
    </picture>
  )
}

///
/// Helper Functions
///

///
// Takes an image url and pulls out the image properties
///
function getImagePropertiesFromURL(image) {
  let url = image;
  const validImageUrlPattern =
    /^(https?:)?\/\/a.storyblok.com\/f\/*[0-9]+\/*[0-9]*x*[0-9]*\/[A-Za-z0-9]+\/[\S]+\.[a-zA-Z]+/;
  const storyblokRegex = /^(https?:)?\/\/a.storyblok.com\//;
  url = validImageUrlPattern.test(url) ? url : "";

  if (!url) {
    return false;
  }

  let originalPath = url.replace(storyblokRegex, "");

  let [, , dimensions, , filename] = originalPath.split("/");
  let [width, height] = dimensions.split("x").map((num) => parseInt(num, 10));
  let [, extension] = filename.split(".");

  let aspectRatio = width / height;
  let metadata = { dimensions: { width, height, aspectRatio } };

  return {
    originalPath,
    extension,
    metadata,
  };
}



///
// This function adds "/m/" to an image filepath for the storyblok image service to convert an image to webp format
// https://www.storyblok.com/docs/image-service#automatic-webp-support-detection
///
function AddStoryblokImageServiceWebpSupport(url) {
  // SB Image can't handle svg images.
  if(!url){return ""}
  if(url.endsWith('.svg')) {
    return url
  }
  let current = `${url}`
  if(current.indexOf('/m/') === -1) {
    if(current.endsWith("/")) {
      current = current+"m/"
    } else {
      current = current+"/m/"
    }
  }

  return current
}

///
// This function add the resize parameters for the storyblok cdn image service
// width and height should be numbers only and not include units (px)
// https://www.storyblok.com/docs/image-service#automatic-webp-support-detection
///
function AddWidthHeightResizeToUrl(url, width, height) {
  if(url.indexOf("/m/") === -1) {
    url = url + "/m/"
  }
  if(url[url.length-1] !== "/") {
    url = url + "/"
  }
  return `${url}${width}x${height}`
}

///
// This function takes a string of breakpoints and outputs an object that Storyblok Image Basic is expecting
///
function ParseBreakpointsFromString(sizes) {
  var bps = sizes
  var bpsObj = {};
  if (sizes !== null && sizes !== "") {
    var arr = bps.split("\n");
    arr.map((bp) => {
      var regex = /px/gm;
      if (bp.includes("px")) {
        bp = bp.replace(regex, "");
      }
      var bpslt = bp.trim().split(":");
      let key = bpslt[0].trim();
      let val = bpslt[bpslt.length - 1].trim();
      return (bpsObj[key] = val);
    });
    return bpsObj;
  }

  return null;
}

///
// Tries to crop an image with respect to the focus point of an image. Will resize if the crop is larger than the image.
// https://www.storyblok.com/docs/image-service#cropping-around-the-focal-point
///
function calcCrop(cropWidth, cropHeight, focusPoint, imageWidth, imageHeight) {
  const w = parseInt(cropWidth),
        h = parseInt(cropHeight);
  const focalPointX = Number(focusPoint.split("x")[0]),
        focalPointY = Number(focusPoint.split("x")[1]);
  let left  = Math.ceil(focalPointX - (w / 2)),
      right = Math.ceil(focalPointX + (w / 2)),
      top   = Math.ceil(focalPointY - (h / 2)),
      bot   = Math.ceil(focalPointY + (h / 2));
  // Check if outside of image size
  if(left < 0) {
    // Slide Right
    let horizontalAdjustment = Math.abs(left);
    left = left + horizontalAdjustment;
    right = right + horizontalAdjustment;
  }
  if(right > imageWidth) {
    // Slide left
    let horizontalAdjustment = imageWidth - right;
    left = left - horizontalAdjustment;
    right = right - horizontalAdjustment;
  }
  if(top < 0) {
    // Slide Down
    let verticalAdjustment = Math.abs(top);
    top = top + verticalAdjustment;
    bot = bot + verticalAdjustment;
  }
  if(bot > imageHeight) {
    // Slide Up
    let verticalAdjustment = imageHeight - bot
    top = top - verticalAdjustment;
    bot = bot - verticalAdjustment;
  }
  
  if (left < 0 || top < 0 || right > imageWidth || bot > imageHeight) {
    console.warn('Image crop does not fit within the provided image dimensions')
    // Returns a resize request instead of a crop
    return `${w}x${h}`;
  }
  return `${left}x${top}:${right}x${bot}`;
}


export { 
  AddStoryblokImageServiceWebpSupport,
  GetImageUrlAndTagSize,
  ParseBreakpointsFromString
}
export default StoryblokImageBasic